/**
 * LeadCaptureFormVR - Displays lead capture forms in VR using Babylon.js GUI
 *
 * Renders 3D form panels for VR headsets with:
 * - Virtual keyboard for text input
 * - Controller-based interaction
 * - Gaze fallback for headsets without controllers
 * - Simplified fields (max 3 in VR for usability)
 */

import { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('LeadCaptureFormVR');
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { InputText } from '@babylonjs/gui/2D/controls/inputText';
import { Button } from '@babylonjs/gui/2D/controls/button';
import { StackPanel } from '@babylonjs/gui/2D/controls/stackPanel';
import { Control } from '@babylonjs/gui/2D/controls/control';
import { Grid } from '@babylonjs/gui/2D/controls/grid';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { HotspotConfig } from '../types/config';
import type {
  LeadFormConfig,
  FormField,
  FormSubmissionResult
} from '../types/forms';

/**
 * Request payload for lead form submission
 */
interface LeadFormRequest {
  sequenceId: string;
  stageId: string | null;
  hotspotId: string | undefined;
  formData: Record<string, string>;
  sessionId: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'vr';
}

// VR Form dimensions
const PANEL_WIDTH = 3; // meters
const PANEL_HEIGHT = 2.5; // meters
const TEXTURE_WIDTH = 1024;
const TEXTURE_HEIGHT = 860;

// Keyboard layout
const KEYBOARD_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '<-'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', '@'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', '.', '_'],
  ['SPACE', 'DONE']
];

/**
 * Analytics callbacks for VR form
 */
export interface VRFormAnalyticsCallbacks {
  onFormViewed?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormSubmitted?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormAbandoned?: (sequenceId: string, hotspotId: string, formType: string) => void;
  onFormError?: (sequenceId: string, hotspotId: string, error: string) => void;
}

export class LeadCaptureFormVR {
  private scene: Scene;
  private sequenceId: string;
  private sessionId: string;

  // VR panel elements
  private formPanel: Mesh | null = null;
  private formTexture: AdvancedDynamicTexture | null = null;
  private keyboardPanel: Mesh | null = null;
  private keyboardTexture: AdvancedDynamicTexture | null = null;

  // Form state
  private currentHotspot: HotspotConfig | null = null;
  private currentStageId: string | null = null;
  private formData: Map<string, string> = new Map();
  private activeInputField: InputText | null = null;
  private activeFieldId: string | null = null;
  private isSubmitting: boolean = false;
  private isShiftActive: boolean = false;

  // Analytics
  private analytics: VRFormAnalyticsCallbacks;

  // Input fields reference
  private inputFields: Map<string, InputText> = new Map();

  constructor(
    scene: Scene,
    sequenceId: string,
    sessionId: string,
    analytics: VRFormAnalyticsCallbacks = {}
  ) {
    this.scene = scene;
    this.sequenceId = sequenceId;
    this.sessionId = sessionId;
    this.analytics = analytics;
  }

  /**
   * Show the VR form for a hotspot
   */
  show(hotspot: HotspotConfig, stageId: string): void {
    if (!hotspot.leadForm?.enabled) {
      logger.warn('Form not enabled for this hotspot');
      return;
    }

    // Clean up any existing panels
    this.dispose();

    this.currentHotspot = hotspot;
    this.currentStageId = stageId;
    this.formData.clear();
    this.inputFields.clear();
    this.isSubmitting = false;

    // Create the form panel
    this.createFormPanel(hotspot.leadForm);

    // Track form view
    this.analytics.onFormViewed?.(
      this.sequenceId,
      hotspot.id,
      hotspot.leadForm.formType
    );
  }

  /**
   * Create the 3D form panel
   */
  private createFormPanel(config: LeadFormConfig): void {
    // Create plane mesh
    this.formPanel = MeshBuilder.CreatePlane(
      'lead-capture-vr-panel',
      { width: PANEL_WIDTH, height: PANEL_HEIGHT },
      this.scene
    );

    // Position in front of camera
    this.positionPanelInFrontOfCamera(this.formPanel);

    // Create texture
    this.formTexture = AdvancedDynamicTexture.CreateForMesh(
      this.formPanel,
      TEXTURE_WIDTH,
      TEXTURE_HEIGHT
    );

    // Create background with glass effect
    const background = new Rectangle('form-background');
    background.width = '100%';
    background.height = '100%';
    background.thickness = 3;
    background.background = 'rgba(30, 30, 40, 0.85)';
    background.color = 'rgba(255, 255, 255, 0.3)';
    background.cornerRadius = 32;
    background.shadowBlur = 40;
    background.shadowColor = 'rgba(0, 0, 0, 0.4)';
    background.shadowOffsetY = 15;
    this.formTexture.addControl(background);

    // Content container
    const content = new StackPanel('form-content');
    content.width = '90%';
    content.paddingTop = '30px';
    content.paddingBottom = '30px';
    content.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    content.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    background.addControl(content);

    // Title
    if (config.title) {
      const title = new TextBlock('form-title', config.title);
      title.height = '60px';
      title.fontSize = 36;
      title.color = 'white';
      title.fontWeight = '700';
      title.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      title.paddingBottom = '10px';
      content.addControl(title);
    }

    // Description
    if (config.description) {
      const description = new TextBlock('form-desc', config.description);
      description.height = '50px';
      description.fontSize = 22;
      description.color = 'rgba(255, 255, 255, 0.7)';
      description.textWrapping = true;
      description.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
      description.paddingBottom = '20px';
      content.addControl(description);
    }

    // Limit to 3 fields in VR for usability
    const fieldsToShow = config.fields
      .filter(f => f.type !== 'hidden')
      .slice(0, 3);

    // Form fields
    fieldsToShow.forEach((field, index) => {
      const fieldContainer = this.createVRField(field, index);
      content.addControl(fieldContainer);

      // Initialize form data
      this.formData.set(field.name, field.defaultValue || '');
    });

    // VR typing notice
    const notice = new TextBlock('vr-notice', 'Tap a field to type');
    notice.height = '35px';
    notice.fontSize = 18;
    notice.color = 'rgba(255, 255, 255, 0.5)';
    notice.paddingTop = '10px';
    content.addControl(notice);

    // Button container
    const buttonContainer = new StackPanel('button-container');
    buttonContainer.height = '80px';
    buttonContainer.isVertical = false;
    buttonContainer.paddingTop = '20px';
    buttonContainer.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    content.addControl(buttonContainer);

    // Close button (if dismissible)
    if (config.allowDismiss !== false) {
      const closeBtn = Button.CreateSimpleButton('close-btn', 'Close');
      closeBtn.width = '180px';
      closeBtn.height = '60px';
      closeBtn.color = 'white';
      closeBtn.background = 'rgba(255, 255, 255, 0.15)';
      closeBtn.cornerRadius = 16;
      closeBtn.fontSize = 22;
      closeBtn.fontWeight = '600';
      closeBtn.paddingRight = '15px';
      closeBtn.onPointerEnterObservable.add(() => {
        closeBtn.background = 'rgba(255, 255, 255, 0.25)';
      });
      closeBtn.onPointerOutObservable.add(() => {
        closeBtn.background = 'rgba(255, 255, 255, 0.15)';
      });
      closeBtn.onPointerClickObservable.add(() => {
        this.hide(true);
      });
      buttonContainer.addControl(closeBtn);
    }

    // Submit button
    const submitBtn = Button.CreateSimpleButton('submit-btn', config.submitButtonText || 'Submit');
    submitBtn.width = '200px';
    submitBtn.height = '60px';
    submitBtn.color = '#1a1a1a';
    submitBtn.background = 'white';
    submitBtn.cornerRadius = 16;
    submitBtn.fontSize = 22;
    submitBtn.fontWeight = '700';
    submitBtn.shadowBlur = 10;
    submitBtn.shadowColor = 'rgba(0, 0, 0, 0.2)';
    submitBtn.shadowOffsetY = 4;
    submitBtn.onPointerEnterObservable.add(() => {
      submitBtn.scaleX = 1.05;
      submitBtn.scaleY = 1.05;
    });
    submitBtn.onPointerOutObservable.add(() => {
      submitBtn.scaleX = 1;
      submitBtn.scaleY = 1;
    });
    submitBtn.onPointerClickObservable.add(() => {
      this.handleSubmit(submitBtn);
    });
    buttonContainer.addControl(submitBtn);
  }

  /**
   * Create a VR form field
   */
  private createVRField(field: FormField, _index: number): StackPanel {
    const container = new StackPanel(`field-${field.id}`);
    container.height = '100px';
    container.paddingBottom = '15px';
    container.width = '100%';

    // Label
    const label = new TextBlock(`label-${field.id}`, field.label + (field.required ? ' *' : ''));
    label.height = '30px';
    label.fontSize = 20;
    label.color = 'rgba(255, 255, 255, 0.9)';
    label.fontWeight = '600';
    label.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    container.addControl(label);

    // Input field
    const input = new InputText(`input-${field.id}`);
    input.width = '100%';
    input.height = '55px';
    input.fontSize = 20;
    input.color = 'white';
    input.background = 'rgba(255, 255, 255, 0.1)';
    input.focusedBackground = 'rgba(255, 255, 255, 0.2)';
    input.thickness = 2;
    input.focusedColor = 'rgba(255, 255, 255, 0.6)';
    input.placeholderText = field.placeholder || '';
    input.placeholderColor = 'rgba(255, 255, 255, 0.4)';
    input.text = field.defaultValue || '';

    // Store reference
    this.inputFields.set(field.id, input);

    // Handle focus - show keyboard
    input.onFocusObservable.add(() => {
      this.activeInputField = input;
      this.activeFieldId = field.id;
      this.showKeyboard();
    });

    // Update form data on change
    input.onTextChangedObservable.add((eventData) => {
      this.formData.set(field.name, eventData.text);
    });

    container.addControl(input);

    return container;
  }

  /**
   * Show virtual keyboard
   */
  private showKeyboard(): void {
    if (this.keyboardPanel) return; // Already showing

    // Create keyboard panel
    this.keyboardPanel = MeshBuilder.CreatePlane(
      'vr-keyboard',
      { width: 2.5, height: 1.2 },
      this.scene
    );

    // Position below form panel
    if (this.formPanel) {
      this.keyboardPanel.position = this.formPanel.position.clone();
      this.keyboardPanel.position.y -= 1.5;
      this.keyboardPanel.rotation = this.formPanel.rotation.clone();
    }

    // Create texture
    this.keyboardTexture = AdvancedDynamicTexture.CreateForMesh(
      this.keyboardPanel,
      800,
      400
    );

    // Background
    const background = new Rectangle('keyboard-bg');
    background.width = '100%';
    background.height = '100%';
    background.background = 'rgba(20, 20, 30, 0.95)';
    background.cornerRadius = 20;
    background.thickness = 2;
    background.color = 'rgba(255, 255, 255, 0.2)';
    this.keyboardTexture.addControl(background);

    // Keyboard grid
    const grid = new Grid('keyboard-grid');
    grid.width = '95%';
    grid.height = '90%';

    // Add rows
    for (let i = 0; i < KEYBOARD_ROWS.length; i++) {
      grid.addRowDefinition(1 / KEYBOARD_ROWS.length);
    }

    // Add columns (max 11 keys per row)
    for (let i = 0; i < 11; i++) {
      grid.addColumnDefinition(1 / 11);
    }

    // Create keys
    KEYBOARD_ROWS.forEach((row, rowIndex) => {
      row.forEach((key, colIndex) => {
        const button = this.createKeyButton(key);

        // Special keys placement (simplified without column span)
        if (key === 'SPACE') {
          // Center the space key
          button.width = '350px';
          grid.addControl(button, rowIndex, 3); // Center of grid
        } else if (key === 'DONE') {
          // Done button on the right
          button.width = '150px';
          grid.addControl(button, rowIndex, 9);
        } else {
          grid.addControl(button, rowIndex, colIndex);
        }
      });
    });

    background.addControl(grid);
  }

  /**
   * Create a keyboard key button
   */
  private createKeyButton(key: string): Button {
    let displayText = key;
    if (key === 'SPACE') displayText = '␣ SPACE';
    if (key === '<-') displayText = '⌫';
    if (key === 'DONE') displayText = '✓ DONE';

    const button = Button.CreateSimpleButton(`key-${key}`, displayText);
    button.width = '95%';
    button.height = '85%';
    button.color = 'white';
    button.fontSize = key === 'SPACE' || key === 'DONE' ? 18 : 24;
    button.fontWeight = '600';
    button.cornerRadius = 8;

    // Styling based on key type
    if (key === 'DONE') {
      button.background = 'rgba(76, 217, 100, 0.8)';
    } else if (key === '<-') {
      button.background = 'rgba(255, 149, 0, 0.8)';
    } else {
      button.background = 'rgba(255, 255, 255, 0.15)';
    }

    // Hover effects
    button.onPointerEnterObservable.add(() => {
      if (key === 'DONE') {
        button.background = 'rgba(76, 217, 100, 1)';
      } else if (key === '<-') {
        button.background = 'rgba(255, 149, 0, 1)';
      } else {
        button.background = 'rgba(255, 255, 255, 0.3)';
      }
    });

    button.onPointerOutObservable.add(() => {
      if (key === 'DONE') {
        button.background = 'rgba(76, 217, 100, 0.8)';
      } else if (key === '<-') {
        button.background = 'rgba(255, 149, 0, 0.8)';
      } else {
        button.background = 'rgba(255, 255, 255, 0.15)';
      }
    });

    // Click handler
    button.onPointerClickObservable.add(() => {
      this.handleKeyPress(key);
    });

    return button;
  }

  /**
   * Handle keyboard key press
   */
  private handleKeyPress(key: string): void {
    if (!this.activeInputField) return;

    const currentText = this.activeInputField.text;

    switch (key) {
      case '<-':
        // Backspace
        if (currentText.length > 0) {
          this.activeInputField.text = currentText.slice(0, -1);
        }
        break;

      case 'SPACE':
        this.activeInputField.text = currentText + ' ';
        break;

      case 'DONE':
        this.hideKeyboard();
        break;

      default: {
        // Regular character
        const char = this.isShiftActive ? key.toUpperCase() : key;
        this.activeInputField.text = currentText + char;
        break;
      }
    }

    // Update form data
    if (this.activeFieldId) {
      const field = this.currentHotspot?.leadForm?.fields.find(f => f.id === this.activeFieldId);
      if (field) {
        this.formData.set(field.name, this.activeInputField.text);
      }
    }
  }

  /**
   * Hide virtual keyboard
   */
  private hideKeyboard(): void {
    if (this.keyboardTexture) {
      this.keyboardTexture.dispose();
      this.keyboardTexture = null;
    }

    if (this.keyboardPanel) {
      this.keyboardPanel.dispose();
      this.keyboardPanel = null;
    }

    this.activeInputField = null;
    this.activeFieldId = null;
  }

  /**
   * Position panel in front of camera
   */
  private positionPanelInFrontOfCamera(panel: Mesh): void {
    const camera = this.scene.activeCamera;
    if (camera) {
      const forward = camera.getForwardRay().direction;
      const position = camera.position.add(forward.scale(2.5));
      panel.position = position;

      // Y-axis only rotation to face camera (keeps panel upright)
      const direction = camera.position.subtract(panel.position);
      const angle = Math.atan2(direction.x, direction.z);
      panel.rotation.y = angle + Math.PI;
      panel.rotation.x = 0;
      panel.rotation.z = 0;
    } else {
      panel.position = new Vector3(0, 1.6, -2.5);
      panel.rotation.y = Math.PI;
    }
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(submitBtn: Button): Promise<void> {
    if (this.isSubmitting || !this.currentHotspot?.leadForm) return;

    // Hide keyboard first
    this.hideKeyboard();

    // Basic validation
    const config = this.currentHotspot.leadForm;
    const errors: string[] = [];

    config.fields.slice(0, 3).forEach(field => {
      if (field.required && field.type !== 'hidden') {
        const value = this.formData.get(field.name) || '';
        if (!value.trim()) {
          errors.push(`${field.label} is required`);
        }
      }

      // Email validation
      if (field.type === 'email') {
        const value = this.formData.get(field.name) || '';
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.push('Please enter a valid email');
        }
      }
    });

    if (errors.length > 0) {
      this.showVRError(errors[0]);
      return;
    }

    // Submit
    this.isSubmitting = true;
    submitBtn.textBlock!.text = 'Submitting...';
    submitBtn.isEnabled = false;

    try {
      const result = await this.submitToBackend();

      if (result.success) {
        this.analytics.onFormSubmitted?.(
          this.sequenceId,
          this.currentHotspot.id,
          config.formType
        );
        this.showVRSuccess(result.message || config.successMessage);
      } else {
        this.showVRError(result.message || 'Submission failed');
        submitBtn.textBlock!.text = config.submitButtonText || 'Submit';
        submitBtn.isEnabled = true;
      }
    } catch (error) {
      logger.error('Submission error', error);
      this.showVRError('An error occurred');
      const errorMessage = error instanceof Error ? error.message : 'Submission exception';
      this.analytics.onFormError?.(
        this.sequenceId,
        this.currentHotspot.id,
        errorMessage
      );
      submitBtn.textBlock!.text = config.submitButtonText || 'Submit';
      submitBtn.isEnabled = true;
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Submit form data to backend
   */
  private async submitToBackend(): Promise<FormSubmissionResult> {
    const functions = getFunctions();
    const submitLeadForm = httpsCallable<LeadFormRequest, FormSubmissionResult>(functions, 'submitLeadForm');

    const formDataObj: Record<string, string> = {};
    this.formData.forEach((value, key) => {
      formDataObj[key] = value;
    });

    const result = await submitLeadForm({
      sequenceId: this.sequenceId,
      stageId: this.currentStageId,
      hotspotId: this.currentHotspot?.id,
      formData: formDataObj,
      sessionId: this.sessionId,
      deviceType: 'vr'
    });

    return result.data;
  }

  /**
   * Show success message in VR
   */
  private showVRSuccess(message: string): void {
    if (!this.formTexture) return;

    // Clear form content
    this.formTexture.rootContainer.children.forEach(child => {
      child.dispose();
    });

    // Create success background
    const background = new Rectangle('success-bg');
    background.width = '100%';
    background.height = '100%';
    background.background = 'rgba(30, 30, 40, 0.9)';
    background.cornerRadius = 32;
    this.formTexture.addControl(background);

    // Success icon
    const icon = new TextBlock('success-icon', '✓');
    icon.fontSize = 120;
    icon.color = 'rgba(76, 217, 100, 1)';
    icon.top = '-80px';
    icon.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    background.addControl(icon);

    // Success message
    const text = new TextBlock('success-text', message);
    text.fontSize = 28;
    text.color = 'white';
    text.textWrapping = true;
    text.width = '80%';
    text.top = '60px';
    text.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
    text.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
    background.addControl(text);

    // Auto-hide after delay
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  /**
   * Show error message in VR
   */
  private showVRError(message: string): void {
    // Find or create error text
    let errorText = this.formTexture?.getControlByName('error-text') as TextBlock;

    if (!errorText && this.formTexture) {
      errorText = new TextBlock('error-text', message);
      errorText.fontSize = 20;
      errorText.color = '#ff6b6b';
      errorText.height = '40px';
      errorText.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
      errorText.paddingBottom = '20px';
      this.formTexture.addControl(errorText);
    } else if (errorText) {
      errorText.text = message;
      errorText.isVisible = true;
    }

    // Hide after delay
    setTimeout(() => {
      if (errorText) {
        errorText.isVisible = false;
      }
    }, 3000);
  }

  /**
   * Hide the VR form
   */
  hide(wasAbandoned: boolean = false): void {
    // Track abandonment
    if (wasAbandoned && this.currentHotspot?.leadForm && !this.isSubmitting) {
      this.analytics.onFormAbandoned?.(
        this.sequenceId,
        this.currentHotspot.id,
        this.currentHotspot.leadForm.formType
      );
    }

    this.dispose();
  }

  /**
   * Check if form is visible
   */
  isVisible(): boolean {
    return this.formPanel !== null;
  }

  /**
   * Get current hotspot
   */
  getCurrentHotspot(): HotspotConfig | null {
    return this.currentHotspot;
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.hideKeyboard();

    if (this.formTexture) {
      this.formTexture.dispose();
      this.formTexture = null;
    }

    if (this.formPanel) {
      this.formPanel.dispose();
      this.formPanel = null;
    }

    this.currentHotspot = null;
    this.currentStageId = null;
    this.formData.clear();
    this.inputFields.clear();
  }
}
